import { useCallback, useMemo } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import {
  Button,
  ConnectionIndicator,
  RadixSelect as Select,
  SearchableSelect,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@/components/ui';
import { useRuns } from '../../hooks/useRuns';
import { useMeAgents } from '../../hooks/useAgents';
import { useMeTools, ToolNameProvider } from '../../hooks/useTools';
import { routes } from '@/lib/routes';
import RunCard from './RunCard';
import RunDetailPanel from './RunDetailPanel';
import type { Agent, RunStatus } from '../../types';
import type { RunSummary } from '../../types/runs';

/** Sentinel for "no filter" — Radix Select disallows empty-string values.
 *  Matches the convention on EventStreamPage. */
const ALL = '__all__';

const STATUS_OPTIONS: { value: string; label: string }[] = [
  { value: ALL, label: 'All Statuses' },
  { value: 'pending', label: 'Pending' },
  { value: 'running', label: 'Running' },
  { value: 'completed', label: 'Completed' },
  { value: 'failed', label: 'Failed' },
  { value: 'error', label: 'Error' },
];

export default function RunsPage() {
  const { runId: urlRunId } = useParams<{ runId: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  // Filters live in the query string so a filtered view is shareable and
  // survives reload — `/c/runs?agentId=agt-…` is the deep link the agent
  // pages' "View agent runs" action points at (routes.console.runsForAgent).
  const [searchParams, setSearchParams] = useSearchParams();
  const agentFilter = searchParams.get('agentId') || ALL;
  const statusFilter = searchParams.get('status') || ALL;
  const hasFilters = agentFilter !== ALL || statusFilter !== ALL;

  const setFilter = useCallback(
    (key: 'agentId' | 'status', value: string) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (value === ALL) next.delete(key);
          else next.set(key, value);
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const clearFilters = useCallback(() => {
    setSearchParams(new URLSearchParams(), { replace: true });
  }, [setSearchParams]);

  const {
    runs,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    realtimeCount,
    isConnected,
  } = useRuns({
    agentId: agentFilter === ALL ? undefined : agentFilter,
    status: statusFilter === ALL ? undefined : (statusFilter as RunStatus),
  });

  const { data: agentsData } = useMeAgents();
  const { data: toolsData } = useMeTools();

  // Agent ID → name lookup
  const agentNameMap = useMemo(() => {
    const m = new Map<string, string>();
    for (const a of agentsData?.items ?? []) {
      m.set(a.agentId, a.name);
    }
    return m;
  }, [agentsData]);

  // Agent ID → space ID lookup. /me/agents rows carry spaceId, even though
  // the Agent TS type doesn't declare it yet.
  const agentSpaceMap = useMemo(() => {
    const m = new Map<string, string>();
    for (const a of agentsData?.items ?? []) {
      const sid = (a as Agent & { spaceId?: string }).spaceId;
      if (sid) m.set(a.agentId, sid);
    }
    return m;
  }, [agentsData]);

  // Tool ID → name lookup for ToolNameProvider
  const toolNameMap = useMemo(() => {
    const m = new Map<string, string>();
    for (const t of toolsData?.items ?? []) {
      m.set(t.toolId, t.name);
    }
    return m;
  }, [toolsData]);

  // Enrich runs with agent names and tool names from lookups
  const enrichedRuns = useMemo(() => {
    return runs.map((r) => ({
      ...r,
      agentName: r.agentName || agentNameMap.get(r.agentId),
      toolCallNames: r.toolCallNames.map((n) => toolNameMap.get(n) ?? n),
    }));
  }, [runs, agentNameMap, toolNameMap]);

  // Options come from the full agent roster (/me/agents, limit 500) rather
  // than from the loaded runs — deriving them from runs meant agents whose
  // runs sat beyond the first page were simply missing from the picker.
  // Any agent seen in a run but absent from /me/agents (the cross-org
  // Architect agent is the known case) is merged back in so nothing drops.
  const agentOptions = useMemo(() => {
    const map = new Map<string, string>();
    for (const a of agentsData?.items ?? []) {
      map.set(a.agentId, a.name);
    }
    for (const r of enrichedRuns) {
      if (r.agentId && !map.has(r.agentId)) {
        map.set(r.agentId, r.agentName ?? r.agentId);
      }
    }
    const opts = [...map.entries()]
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));
    return [{ value: ALL, label: 'All Agents' }, ...opts];
  }, [agentsData, enrichedRuns]);

  const agentFilterLabel =
    agentFilter === ALL
      ? null
      : agentOptions.find((o) => o.value === agentFilter)?.label ?? agentFilter;

  // The server already applies both filters (see useRuns). This predicate is
  // the guard for live runs: useRuns merges WebSocket runs into the list
  // regardless of the active filter, so without it a run belonging to another
  // agent would pop into a filtered view.
  const filtered = useMemo(() => {
    return enrichedRuns.filter((r) => {
      if (statusFilter !== ALL && r.status !== statusFilter) return false;
      if (agentFilter !== ALL && r.agentId !== agentFilter) return false;
      return true;
    });
  }, [enrichedRuns, statusFilter, agentFilter]);

  // Carry the filter query string through the detail panel so closing it
  // returns to the same filtered list rather than resetting to all runs.
  function openDetail(run: RunSummary) {
    navigate(`${routes.console.run(run.runId)}${location.search}`);
  }

  function closeDetail() {
    navigate(`${routes.console.runs}${location.search}`);
  }

  // Look up the URL-selected run in the loaded list (REST + realtime).
  // /me/runs has no per-id read, so cold deep-links to runs that aren't on
  // the loaded page can't render a detail panel — pending backend support.
  const listedRun = urlRunId
    ? enrichedRuns.find((r) => r.runId === urlRunId)
    : null;

  const displayRun: RunSummary | null = listedRun ?? null;
  // Prefer the run's own owning space (returned by /me/runs) so the per-run
  // detail read works even when the run's agent isn't in /me/agents (e.g. the
  // global/cross-org Architect agent); fall back to the agent→space map.
  const runSpaceId = listedRun
    ? listedRun.spaceId ?? agentSpaceMap.get(listedRun.agentId)
    : undefined;
  const panelOpen = !!urlRunId;

  return (
    <ToolNameProvider value={toolNameMap}>
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-semibold">Runs</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Monitor agent execution runs across your organization
        </p>
      </div>

      {/* Filter bar */}
      <div className="flex items-center gap-4 mb-3 flex-wrap">
        <Select
          value={statusFilter}
          onValueChange={(v) => setFilter('status', v)}
        >
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="All Statuses" />
          </SelectTrigger>
          <SelectContent>
            {STATUS_OPTIONS.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <SearchableSelect
          value={agentFilter}
          onChange={(v) => setFilter('agentId', v)}
          options={agentOptions}
          placeholder="All Agents"
          searchPlaceholder="Search agents..."
          emptyMessage="No agents found."
          aria-label="Filter by agent"
          className="w-[260px]"
        />

        <ConnectionIndicator isConnected={isConnected} className="ml-auto" />
      </div>

      {/* Count */}
      <div className="flex items-center gap-2 mb-3">
        <p className="text-xs text-muted-foreground">
          Showing {filtered.length} run{filtered.length === 1 ? '' : 's'}
          {agentFilterLabel && ` for ${agentFilterLabel}`}
          {realtimeCount > 0 && ` (${realtimeCount} new)`}
        </p>
        {hasFilters && (
          <Button
            variant="ghost"
            size="sm"
            onClick={clearFilters}
            className="h-6 px-2 text-xs text-muted-foreground"
          >
            Clear filters
          </Button>
        )}
      </div>

      {/* Content: side-by-side layout */}
      <div className="flex flex-1 min-h-0 overflow-hidden">
        {/* Run list */}
        <div className={`${panelOpen ? 'w-1/2' : 'w-full'} transition-all duration-200 overflow-y-auto`}>
          {isLoading ? (
            <div className="flex items-center justify-center h-40">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="flex items-center justify-center h-40 text-muted-foreground text-sm">
              No runs yet
            </div>
          ) : (
            <div className="space-y-3 pr-2">
              {filtered.map((run, idx) => (
                <RunCard
                  key={run.runId}
                  run={run}
                  isRealtime={idx < realtimeCount}
                  isSelected={urlRunId === run.runId}
                  runSpaceId={run.spaceId ?? agentSpaceMap.get(run.agentId)}
                  onClick={() => openDetail(run)}
                />
              ))}

              {hasNextPage && (
                <div className="flex justify-center py-4">
                  <Button
                    variant="outline"
                    onClick={() => fetchNextPage()}
                    disabled={isFetchingNextPage}
                  >
                    {isFetchingNextPage ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                        Loading...
                      </>
                    ) : (
                      'Load More'
                    )}
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Detail panel */}
        {panelOpen && displayRun && (
          <div className="w-1/2 border-l border-border overflow-y-auto">
            <RunDetailPanel run={displayRun} runSpaceId={runSpaceId} onClose={closeDetail} />
          </div>
        )}
        {panelOpen && !displayRun && isLoading && (
          <div className="flex w-1/2 items-center justify-center border-l border-border">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        )}
        {panelOpen && !displayRun && !isLoading && (
          <div className="flex w-1/2 items-center justify-center border-l border-border p-6 text-sm text-muted-foreground">
            Couldn't load run {urlRunId}.
          </div>
        )}
      </div>
    </div>
    </ToolNameProvider>
  );
}
